/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `Unsubscribe` for the Caintra Connect frontend.
 * @module pages/Unsubscribe
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";

import styles from "../styles/Unsubscribe.module.css";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import { unsubscribeFromCustomEmails } from "../logic/customNotification/index.js";

import { ReactComponent as CaintraLogo } from "../assets/Caintra_Logo.svg";
import { ReactComponent as ChevronLeftIcon } from "../assets/icons/CompanyEditPage/ChevronLeftIcon.svg";
import { CircularProgress } from "@mui/material";

const Unsubscribe = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading | success | already | error | missing

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setStatus("missing");
      return;
    }

    let cancelled = false;

    const run = async () => {
      try {
        const result = await unsubscribeFromCustomEmails(token);
        if (cancelled) {
          return;
        }
        if (result?.code === "ALREADY_UNSUBSCRIBED") {
          setStatus("already");
        } else if (result?.code === "INVALID_TOKEN") {
          setStatus("invalid");
        } else if (result?.ok) {
          setStatus("success");
        } else {
          setStatus("error");
        }
      } catch (error) {
        logger.error("Error", { err: sanitizeError(error) });
        if (!cancelled) {
          setStatus("error");
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const handleBack = () => {
    const token = localStorage.getItem("token");
    if (token) {
      navigate("/home");
    } else {
      navigate("/");
    }
  };

  const titleKey =
    status === "success"
      ? "pages.unsubscribe.successTitle"
      : status === "already"
        ? "pages.unsubscribe.alreadyTitle"
        : status === "missing"
          ? "pages.unsubscribe.missingTitle"
          : status === "invalid"
            ? "pages.unsubscribe.invalidTitle"
            : status === "error"
              ? "pages.unsubscribe.errorTitle"
              : "pages.unsubscribe.loadingTitle";

  const messageKey =
    status === "success"
      ? "pages.unsubscribe.successMessage"
      : status === "already"
        ? "pages.unsubscribe.alreadyMessage"
        : status === "missing"
          ? "pages.unsubscribe.missingMessage"
          : status === "invalid"
            ? "pages.unsubscribe.invalidMessage"
            : status === "error"
              ? "pages.unsubscribe.errorMessage"
              : "pages.unsubscribe.loadingMessage";

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.returnButtonContainer} onClick={handleBack}>
          <ChevronLeftIcon className={styles.returnButtonIcon} />
          <span className={styles.returnButtonText}>{t("common.goBack")}</span>
        </button>
        <LanguageSelector />
      </div>
      <div className={styles.imgContainer}>
        <CaintraLogo className={styles.logo} />
      </div>
      <div className={styles.mainContainer}>
        <h1 className={styles.title}>{t(titleKey)}</h1>
        {status === "loading" ? (
          <CircularProgress />
        ) : (
          <p className={styles.message}>{t(messageKey)}</p>
        )}
      </div>
    </div>
  );
};

export default Unsubscribe;
